import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import CouponList from './coupon/CouponList';
import { Coupon } from './coupon/coupon';
import CouponService from '../../service/coupon/Coupon';
import ConfirmAlert from '../components/alert/ConfirmAlert';
import { useModal } from '../../context/ModalContext';
import Alert from '../components/alert/Alert';

type RegisterState = 'confirm' | 'disable' | 'overlap' | 'expiration' | null;

function CouponBox() {
  const { modalDispatch } = useModal();
  const [selectedTab, setSelectedTab] = useState<'미사용' | '사용 완료'>(
    '미사용',
  );
  const [couponList, setCouponList] = useState<Coupon[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [couponText, setCouponText] = useState('');
  const [registerState, setRegisterState] = useState<RegisterState>(null);

  const handleRegister = async () => {
    const code = couponText.trim();
    if (!code) return;

    try {
      await CouponService.registerCode(code);
      // 등록 완료
      setRegisterState('confirm');
      setCouponText('');
      getList();
    } catch (error: any) {
      const msg = error?.response?.data?.message ?? '';
      if (msg.includes('이미 등록된 쿠폰')) {
        setRegisterState('overlap'); // 중복된 쿠폰
      } else if (msg.includes('유효하지') || msg.includes('존재하지')) {
        setRegisterState('disable'); // 유효하지 않은 쿠폰 번호
      } else {
        setRegisterState('disable');
      }
    }
  };
  const getList = async () => {
    try {
      const res = await CouponService.list(
        selectedTab === '미사용' ? 'A' : 'B',
        currentPage,
      );
      setCouponList(res.data.list);
      setTotalCount(res.data.totalCount);
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    getList();
  }, [selectedTab, currentPage]);

  const RegisterModal = () => {
    const onClose = () => setRegisterState(null);

    switch (registerState) {
      case 'confirm':
        return <Alert text="쿠폰 등록이 완료됐어요." onClose={onClose} />;
      case 'disable':
        return (
          <Alert
            text={`등록되지 않은 쿠폰 번호입니다.\n쿠폰 번호를 확인해 주세요.`}
            onClose={onClose}
          />
        );
      case 'expiration':
        return <Alert text="등록 기간이 지난 쿠폰이에요." onClose={onClose} />;
      case 'overlap':
        return (
          <ConfirmAlert
            title={`이미 사용된 쿠폰 번호입니다.\n도움이 필요한 경우 고객센터로 문의해 주세요.`}
            grayButtonText="문의하기"
            blueButtonText="확인"
            onGrayClick={() => modalDispatch({ type: 'I' })}
            onClose={onClose}
          />
        );
    }
  };

  const sortedCoupons =
    selectedTab === '미사용'
      ? [...couponList].sort((a, b) => {
          if (a.is_possible !== b.is_possible) {
            return a.is_possible ? -1 : 1;
          }
          const aHasDiscount = a.name.includes('할인쿠폰');
          const bHasDiscount = b.name.includes('할인쿠폰');
          if (aHasDiscount !== bHasDiscount) {
            return aHasDiscount ? -1 : 1;
          }
          return 0;
        })
      : couponList;

  return (
    <Container>
      <RegisterContainer>
        <RegisterInputBox>
          <RegisterInput
            value={couponText}
            onChange={(e) => setCouponText(e.target.value)}
            placeholder="쿠폰 번호를 입력해 주세요."
          />
          {couponText && (
            <DeleteButton
              src="/assets/images/close/close-circle.svg"
              alt=""
              onClick={() => setCouponText('')}
            />
          )}
        </RegisterInputBox>
        <RegisterButton disabled={couponText === ''} onClick={handleRegister}>
          쿠폰등록
        </RegisterButton>
      </RegisterContainer>
      <TabBoxContainer>
        <TabBox>
          {['미사용', '사용 완료'].map((label) => (
            <Tab
              key={label}
              selected={selectedTab === label}
              onClick={() => setSelectedTab(label as '미사용' | '사용 완료')}
            >
              {label}
            </Tab>
          ))}
        </TabBox>
      </TabBoxContainer>
      {couponList.length > 0 ? (
        <CouponList
          activeTab={selectedTab}
          couponList={sortedCoupons}
          getList={getList}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalCount={totalCount}
        />
      ) : (
        <EmptyMessage>
          {selectedTab === '미사용'
            ? '현재 이용 가능한 쿠폰이 없습니다.'
            : '이용 내역이 없습니다.'}
        </EmptyMessage>
      )}
      {registerState !== null && RegisterModal()}
    </Container>
  );
}

export default CouponBox;

const Container = styled.div`
  padding: 0 16px;
  min-height: 80dvh;
`;

const RegisterContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  padding: 8px 0 16px;
  width: 100%;
`;

const RegisterInputBox = styled.div`
  display: flex;
  flex-direction: row;
  height: 50px;
  padding: 4px 16px;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex: 1 0 0;
  border: 1px solid #e1e2e4;
  background: #fff;
  border-radius: 8px;

  &:focus-within {
    border-color: #008aff;
  }
`;

const RegisterInput = styled.input`
  min-width: 0;
  flex: 1 0 0;
  border: none;
  background: #fff;
  outline: none;

  font-size: 16px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.32px;

  &::placeholder {
    color: rgba(0, 25, 54, 0.3);
  }
`;

const DeleteButton = styled.img`
  cursor: pointer;
  width: 20px;
  height: 20px;
`;

const RegisterButton = styled.button`
  display: flex;
  padding: 12px 16px;
  justify-content: center;
  align-items: center;
  align-self: stretch;
  border-radius: 8px;
  border: 1px solid #e1e2e4;
  background: #fff;
  cursor: pointer;

  color: #008aff;
  text-align: center;
  font-size: 14px;
  font-weight: 5500;
  line-height: 145%;
  letter-spacing: -0.28px;

  &:hover {
    border: 1px solid #008aff;
    background: #f0f8ff;
  }

  &:disabled {
    background: #fff;
    color: rgba(0, 25, 54, 0.31);
    border-color: #e1e2e4;
    pointer-events: none;
  }
`;

const TabBoxContainer = styled.div`
  display: flex;
  padding: 8px 0;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  align-self: stretch;
`;

const TabBox = styled.div`
  display: flex;
  flex-direction: row;
  border-radius: 8px;
  padding: 4px;
  background: #70737c0d;
  align-self: stretch;
`;

const Tab = styled.div<{ selected: boolean }>`
  flex: 1;
  padding: 8px 16px;
  border-radius: 8px;
  display: flex;
  justify-content: center;
  align-items: center;

  background-color: ${({ selected }) => (selected ? '#ffffff' : 'transparent')};
  color: ${({ selected }) => (selected ? '#222222' : '#989BA2')};
  box-shadow: ${({ selected }) =>
    selected
      ? `
    0px 0px 1px 0px #383A3B14,
    0px 1px 4px 0px #383A3B14,
    0px 2px 8px 0px #383A3B1F
  `
      : 'none'};
  cursor: pointer;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0%;
`;

const ContentBox = styled.div`
  background-color: #f7f8fc;
  padding: 24px 16px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const EmptyMessage = styled.div`
  text-align: center;
  font-size: 14px;
  font-weight: 500;
  color: #616161;
  padding: 40px 0;
`;
